import React, { useState, useEffect } from 'react';
import { Stack } from '@mui/material';
import EditableSelect from 'src/components/shared/editable-components/EditableSelect';
import EditableValueView from 'src/components/shared/editable-components/EditableValueView';
import EditableTextField from 'src/components/shared/editable-components/EditableTextField';

const SingleValueEditBox = ({
    title = 'Value',
    value,
    onChange,
    onSave,
    loading,
    success,
    error,
    disabled = false,
    readOnly = false,
    bold = true,
    type = 'text',
    options = [],
    titleValueGap = 0,
    isNumeric = false,
    decimalPlaces = 2,
    allowDecimals = true,
}) => {
    const [editMode, setEditMode] = useState(false);
    const [currentValue, setCurrentValue] = useState(value);
    const [originalValue, setOriginalValue] = useState(value);
    const [hasChanges, setHasChanges] = useState(false);

    const handleEdit = () => {
        if (disabled || readOnly) return;
        setEditMode(true);
        setCurrentValue(value);
        setOriginalValue(value);
        setHasChanges(false);
    };

    const handleCancel = () => {
        onChange(originalValue);
        setEditMode(false);
        setHasChanges(false);
    };

    const handleChange = (val) => {
        if (isNumeric && val) {
            const regex = allowDecimals ? /^[0-9]*\.?[0-9]*$/ : /^[0-9]*$/;
            if (!regex.test(val) && val !== '') return;
        }
        
        onChange(val);
        setCurrentValue(val);
        setHasChanges(val !== originalValue);
    };

    const handleSaveWithFormat = async () => {
        let valueToSave = currentValue;
        
        // Format numeric value before saving
        if (isNumeric && currentValue && currentValue.trim() !== '') {
            const numValue = parseFloat(currentValue);
            if (!isNaN(numValue)) {
                valueToSave = numValue.toFixed(decimalPlaces);
                onChange(valueToSave);
            }
        }
        
        await onSave();
    };

    const handleKeyDown = async (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (currentValue.trim() === '') return;
            await handleSaveWithFormat();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleCancel();
        }
    };

    useEffect(() => {
        if (!error && !loading && success) {
            setEditMode(false);
            setHasChanges(false);
        }
    }, [error, loading, success]);

    useEffect(() => {
        if (!editMode) {
            setCurrentValue(value);
            setOriginalValue(value);
            setHasChanges(false);
        }
    }, [value, editMode]);

    return (
        <Stack direction="row" spacing={2}>
            {editMode ? (
                type === 'select' ? (
                    <EditableSelect
                        title={title}
                        value={currentValue}
                        options={options}
                        loading={loading}
                        success={success}
                        error={error}
                        isRequired={true}
                        disabled={disabled}
                        readOnly={readOnly}
                        onChange={handleChange}
                        onSave={handleSaveWithFormat}
                        onCancel={handleCancel}
                        onKeyDown={handleKeyDown}
                        hasChanges={hasChanges}
                    />
                ) : (
                    <EditableTextField
                        title={title}
                        value={currentValue}
                        loading={loading}
                        success={success}
                        error={error}
                        isRequired={true}
                        disabled={disabled}
                        readOnly={readOnly}
                        onChange={handleChange}
                        onSave={handleSaveWithFormat}
                        onCancel={handleCancel}
                        onKeyDown={handleKeyDown}
                        hasChanges={hasChanges}
                        inputMode={isNumeric ? "decimal" : "text"}
                    />
                )
            ) : (
                <EditableValueView
                    title={title}
                    value={value}
                    onEdit={handleEdit}
                    disabled={disabled}
                    readOnly={readOnly}
                    bold={bold}
                    titleValueGap={titleValueGap}
                    isNumeric={isNumeric}
                    decimalPlaces={decimalPlaces}
                />
            )}
        </Stack>
    );
};

export default SingleValueEditBox;
